import React, { useState, useEffect, useRef } from 'react';
import {
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
  TableCellsIcon,
  ListBulletIcon,
  DocumentTextIcon,
  ShareIcon,
  GlobeAltIcon,
  UsersIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import { FormTemplate, ListTemplate } from '../../types/formBuilder';
import { useLanguage } from '../../contexts/LanguageContext';
import { userService } from '../../services/userService';
import { TemplateService } from '../../services/templateService';
import { formatDistanceToNow } from 'date-fns';
import { hu, enUS } from 'date-fns/locale';
import ConfirmDialog from '../common/ConfirmDialog';
import ImportModal from './ImportModal';

interface TemplateListProps {
  templates: (FormTemplate | ListTemplate)[];
  templateType: 'form' | 'list';
  displayMode?: 'grid' | 'list';
  onEdit?: (template: FormTemplate | ListTemplate) => void;
  onDelete?: (templateId: string) => void;
  onDuplicate?: (template: FormTemplate | ListTemplate) => void;
  onView?: (template: FormTemplate | ListTemplate) => void;
  onFill?: (formTemplate: FormTemplate) => void;
  onShare?: (formTemplate: FormTemplate) => void;
  onCreateListForForm?: (formTemplate: FormTemplate) => void;
  onViewFormLists?: (formTemplate: FormTemplate) => void;
  onExport?: (template: FormTemplate) => void;
  onImport?: (templates: Partial<FormTemplate>[], organizationId: string, addSuffix?: boolean) => Promise<void>;
  onExportAll?: (templates: FormTemplate[]) => void;
}

const TemplateList: React.FC<TemplateListProps> = ({
  templates,
  templateType,
  displayMode = 'grid',
  onEdit,
  onDelete,
  onDuplicate,
  onView,
  onFill,
  onShare,
  onCreateListForForm,
  onViewFormLists,
  onExport,
  onImport,
  onExportAll
}) => {
  const { t, language } = useLanguage();
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    template: FormTemplate | ListTemplate | null;
  }>({ isOpen: false, template: null });

  const [importModal, setImportModal] = useState<{
    isOpen: boolean;
    templates: Partial<FormTemplate>[];
    isMultiple: boolean;
  }>({
    isOpen: false,
    templates: [],
    isMultiple: false
  });

  useEffect(() => {
    loadUserNames();
  }, [templates]);

  const loadUserNames = async () => {
    const uniqueUserIds = Array.from(new Set(templates.map(t => t.createdBy)));
    const newUserNames: Record<string, string> = {};

    for (const userId of uniqueUserIds) {
      if (!userNames[userId] && !userId.includes('@')) { // Skip if already loaded or if it's an email
        try {
          const userData = await userService.getUser(userId);
          if (userData) {
            newUserNames[userId] = userData.displayName || userData.email || 'Unknown User';
          } else {
            newUserNames[userId] = `User ${userId.slice(-6)}`;
          }
        } catch (error) {
          console.error('Error loading user data:', error);
          newUserNames[userId] = `User ${userId.slice(-6)}`;
        }
      }
    }

    if (Object.keys(newUserNames).length > 0) {
      setUserNames(prev => ({ ...prev, ...newUserNames }));
    }
  };

  const formatDate = (date: Date) => {
    const formatted = formatDistanceToNow(date, {
      addSuffix: true,
      locale: language === 'hu' ? hu : enUS
    });
    
    if (language === 'hu') {
      return formatted
        .replace('körülbelül ', '')
        .replace(' ezelőtt', '')
        .replace('órával', 'órája')
        .replace('nappal', 'napja')
        .replace('héttel', 'hete')
        .replace('hónappal', 'hónapja')
        .replace('évvel', 'éve')
        .replace('perccel', 'perce')
        .replace('másodperccel', 'másodperce');
    }
    
    return formatted;
  };

  const getTemplateIcon = (template: FormTemplate | ListTemplate) => {
    if ('fields' in template) {
      return '📝'; // Form icon
    } else {
      return '📊'; // List icon
    }
  };

  const getFieldCount = (template: FormTemplate | ListTemplate) => {
    if ('fields' in template) {
      return `${template.fields.length} ${t('formBuilder.properties.fields') || 'fields'}`;
    } else {
      return `${template.columns.length} ${t('listBuilder.columns') || 'columns'}`;
    }
  };

  // Form template importálása
  const handleImportTemplate = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !onImport) return;
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const jsonData = e.target?.result as string;
        const importedTemplates = TemplateService.importMultipleFormTemplates(jsonData);
        
        // Open import modal with the templates
        setImportModal({
          isOpen: true,
          templates: importedTemplates,
          isMultiple: importedTemplates.length > 1
        });
      } catch (error) {
        console.error('Error importing form template:', error);
        alert(`Hiba történt az importálás során: ${error instanceof Error ? error.message : 'Ismeretlen hiba'}`);
      }
    };
    reader.readAsText(file);
    
    // Reset input
    event.target.value = '';
  };

  // Import modal bezárása
  const handleCloseImportModal = () => {
    setImportModal({
      isOpen: false,
      templates: [],
      isMultiple: false
    });
  };

  // Import végrehajtása a kiválasztott szervezetbe
  const handleConfirmImport = async (templates: Partial<FormTemplate>[], organizationId: string, addSuffix?: boolean) => {
    if (onImport) {
      await onImport(templates, organizationId, addSuffix);
    }
  };

  if (templates.length === 0) {
    return (
      <>
        {/* Import Button - mindig megjelenik, még üres állapotban is */}
        {templateType === 'form' && onImport && (
          <div className="mb-6 flex justify-end">
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImportTemplate}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
              Sablon(ok) importálása
            </button>
          </div>
        )}

        <div className="text-center py-12">
          <div className="text-6xl mb-4">
            {templateType === 'form' ? '📝' : '📊'}
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {t('formBuilder.templates.noTemplates') || 'No templates found'}
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {t('formBuilder.templates.createFirst') || 'Create your first template to get started'}
          </p>
          {templateType === 'form' && onImport && (
            <p className="text-sm text-blue-600 dark:text-blue-400 mt-3">
              💡 Vagy importálj meglévő sablonokat a fenti gombbal
            </p>
          )}
        </div>

        {/* Import Modal - üres állapotban is szükséges */}
        <ImportModal
          isOpen={importModal.isOpen}
          onClose={handleCloseImportModal}
          onImport={handleConfirmImport}
          importedTemplates={importModal.templates}
          isMultiple={importModal.isMultiple}
        />
      </>
    );
  }

  return (
    <>
      {/* Import/Export Buttons */}
      {templateType === 'form' && (onImport || onExportAll) && (
        <div className="mb-4 flex justify-end items-center gap-2">
          {onExportAll && templates.length > 0 && (
            <button
              onClick={() => onExportAll(templates as FormTemplate[])}
              className="group relative inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg overflow-hidden transition-all duration-300 hover:scale-105"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-green-400/20 to-emerald-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <ArrowDownTrayIcon className="h-3.5 w-3.5 mr-1.5 relative z-10 text-green-600 dark:text-green-400" />
              <span className="relative z-10 text-gray-700 dark:text-gray-200">Export JSON</span>
            </button>
          )}
          
          {onImport && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImportTemplate}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="group relative inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg overflow-hidden transition-all duration-300 hover:scale-105"
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <ArrowUpTrayIcon className="h-3.5 w-3.5 mr-1.5 relative z-10 text-blue-600 dark:text-blue-400" />
                <span className="relative z-10 text-gray-700 dark:text-gray-200">Import JSON</span>
              </button>
            </>
          )}
        </div>
      )}

      <div className={displayMode === 'grid' 
        ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" 
        : "space-y-3"
      }>
        {templates.map((template) => (
        <div
          key={template.id}
          className={`bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:shadow-md transition-shadow ${
            displayMode === 'grid' ? 'p-6' : 'p-4'
          }`}
        >
          {/* Header */}
          <div className={`flex items-start gap-3 ${displayMode === 'grid' ? 'mb-4' : 'mb-2'}`}>
            <div className={`flex items-center gap-3 flex-1 min-w-0`}>
              <div className={`flex-shrink-0 ${displayMode === 'grid' ? 'text-2xl' : 'text-xl'}`}>
                {getTemplateIcon(template)}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className={`font-semibold text-gray-900 dark:text-gray-100 ${
                  displayMode === 'grid' ? 'text-lg' : 'text-base'
                } ${displayMode === 'grid' ? 'line-clamp-2' : 'truncate'}`}
                  title={template.name}
                >
                  {template.name}
                </h3>
                <p className={`text-gray-500 dark:text-gray-400 truncate ${
                  displayMode === 'grid' ? 'text-sm' : 'text-xs'
                }`}>
                  {getFieldCount(template)}
                </p>
              </div>
            </div>
            
            {/* Status Badges */}
            <div className="flex flex-col gap-1 items-end flex-shrink-0">
              <span className={`
                inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                ${template.isActive
                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                }
              `}>
                {template.isActive 
                  ? (t('formBuilder.templates.active') || 'Active')
                  : (t('formBuilder.templates.inactive') || 'Inactive')
                }
              </span>
              
              {/* Customer Portal Badges - Only for forms */}
              {'fields' in template && (template as FormTemplate).customerPortalSettings?.isAvailableOnPortal && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                  <UsersIcon className="h-3 w-3 mr-1" />
                  {(template as FormTemplate).customerPortalSettings?.allowAllCustomers 
                    ? 'Minden ügyfél' 
                    : `${(template as FormTemplate).customerPortalSettings?.allowedCustomers?.length || 0} ügyfél`
                  }
                </span>
              )}
              
              {/* Public Sharing Badge - Only for forms */}
              {'fields' in template && (template as FormTemplate).publicSettings?.isPublic && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  <GlobeAltIcon className="h-3 w-3 mr-1" />
                  Nyilvános
                </span>
              )}
            </div>
          </div>

          {/* Description */}
          {displayMode === 'grid' && template.description && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
              {template.description}
            </p>
          )}

          {/* Category & Tags */}
          <div className={`flex flex-wrap gap-2 ${displayMode === 'grid' ? 'mb-4' : 'mb-2'}`}>
            {template.category && (
              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                <TagIcon className="h-3 w-3 mr-1" />
                {template.category}
              </span>
            )}
            {'tags' in template && template.tags?.slice(0, 2).map((tag: string) => (
              <span
                key={tag}
                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
              >
                {tag}
              </span>
            ))}
            {'tags' in template && template.tags && template.tags.length > 2 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                +{template.tags.length - 2} more
              </span>
            )}
          </div>

          {/* Metadata */}
          {displayMode === 'grid' && (
            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <CalendarIcon className="h-3 w-3 mr-1" />
                  {formatDate(template.updatedAt)}
                </div>
                <div className="flex items-center">
                  <UserIcon className="h-3 w-3 mr-1" />
                  {template.createdByName || userNames[template.createdBy] || `User ${template.createdBy.slice(-6)}`}
                </div>
              </div>
              <div className="text-xs">
                v{'version' in template ? template.version : 1}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className={`flex items-center justify-between ${displayMode === 'grid' ? 'pt-4 border-t border-gray-200 dark:border-gray-600' : 'pt-2'}`}>
            <div className="flex space-x-2">
              {onView && (
                <button
                  onClick={() => onView(template)}
                  className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  title={templateType === 'list' ? (t('listViewer.viewData') || 'View Data') : (t('common.buttons.view') || 'View')}
                >
                  <EyeIcon className="h-4 w-4" />
                </button>
              )}
              
              {onEdit && (
                <button
                  onClick={() => onEdit(template)}
                  className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  title={t('common.buttons.edit') || 'Edit'}
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              )}
              
              {onDuplicate && (
                <button
                  onClick={() => onDuplicate(template)}
                  className="p-2 text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                  title={t('formBuilder.templates.duplicate') || 'Duplicate'}
                >
                  <DocumentDuplicateIcon className="h-4 w-4" />
                </button>
              )}

              {/* Form-specific actions */}
              {templateType === 'form' && onFill && (
                <button
                  onClick={() => onFill(template as FormTemplate)}
                  className="p-2 text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                  title={t('myForms.fill') || 'Fill Form'}
                >
                  <DocumentTextIcon className="h-4 w-4" />
                </button>
              )}

              {templateType === 'form' && onShare && (
                <button
                  onClick={() => onShare(template as FormTemplate)}
                  className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  title={t('formBuilder.share.title') || 'Share Form'}
                >
                  <ShareIcon className="h-4 w-4" />
                </button>
              )}

              {templateType === 'form' && onCreateListForForm && (
                <button
                  onClick={() => onCreateListForForm(template as FormTemplate)}
                  className="p-2 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                  title={t('formBuilder.templates.createList') || 'Create List'}
                >
                  <TableCellsIcon className="h-4 w-4" />
                </button>
              )}

              {templateType === 'form' && onViewFormLists && (
                <button
                  onClick={() => onViewFormLists(template as FormTemplate)}
                  className="p-2 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  title={t('formBuilder.templates.viewLists') || 'View Lists'}
                >
                  <ListBulletIcon className="h-4 w-4" />
                </button>
              )}

              {/* Export/Import actions for forms */}
              {templateType === 'form' && onExport && (
                <button
                  onClick={() => onExport(template as FormTemplate)}
                  className="p-2 text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 transition-colors"
                  title="Exportálás"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            
            {onDelete && (
              <button
                onClick={() => setDeleteConfirm({ isOpen: true, template })}
                className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                title={t('common.buttons.delete') || 'Delete'}
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, template: null })}
        onConfirm={() => {
          if (deleteConfirm.template && onDelete) {
            onDelete(deleteConfirm.template.id);
          }
          setDeleteConfirm({ isOpen: false, template: null });
        }}
        title={t('formBuilder.confirmDeleteTemplate.title') || 'Delete Template'}
        message={t('formBuilder.confirmDeleteTemplate.message') || 'Are you sure you want to delete this template? This action cannot be undone.'}
        itemName={deleteConfirm.template?.name}
        confirmText={t('formBuilder.confirmDeleteTemplate.confirm') || 'Delete Template'}
        cancelText={t('common.buttons.cancel') || 'Cancel'}
        type="danger"
      />

      {/* Import Modal */}
      <ImportModal
        isOpen={importModal.isOpen}
        onClose={handleCloseImportModal}
        onImport={handleConfirmImport}
        importedTemplates={importModal.templates}
        isMultiple={importModal.isMultiple}
      />
    </>
  );
};

export default TemplateList;
